import { logger } from '../../utils/logger';
import { getEnv } from '../../config/env';
import type { SnapshotHistoryEntry } from '../../types';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';

/**
 * Fetches snapshot history for a recurring snapshot
 * @param accessToken - Bearer token for authentication
 * @param migrationId - ID of the snapshot to fetch history for
 * @returns Array of snapshot history entries, sorted newest first
 */
export async function fetchSnapshotHistory(
  accessToken: string,
  migrationId: string
): Promise<SnapshotHistoryEntry[]> {
  const env = getEnv();
  const baseUrl = env.VITE_EDCI_API_URL.replace(/\/$/, '');
  const url = `${baseUrl}/api/migrations/${migrationId}/snapshot-history`;

  logger.info('Fetching snapshot history', { migrationId });

  try {
    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: getV2AuthHeaders(accessToken)
    });

    if (!response.ok) {
      logger.error('Failed to fetch snapshot history', {
        migrationId,
        status: response.status,
        statusText: response.statusText
      });
      throw new Error(`Failed to fetch snapshot history: ${response.statusText}`);
    }

    const history: SnapshotHistoryEntry[] = await response.json();
    logger.info('Snapshot history fetched successfully', { migrationId, count: history.length });
    return history;
  } catch (error) {
    logger.error('Error fetching snapshot history', { migrationId, error });
    throw error;
  }
}

/**
 * Downloads a specific historical snapshot as a ZIP file
 * @param accessToken - Bearer token for authentication
 * @param migrationId - ID of the snapshot
 * @param timestamp - Timestamp of the specific snapshot run to download
 */
export async function downloadHistoricalSnapshot(
  accessToken: string,
  migrationId: string,
  timestamp: string
): Promise<void> {
  const env = getEnv();
  const baseUrl = env.VITE_EDCI_API_URL.replace(/\/$/, '');
  const url = `${baseUrl}/api/migrations/${migrationId}/snapshots/${timestamp}/download`;

  logger.info('Downloading historical snapshot', { migrationId, timestamp });

  try {
    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: getV2AuthHeaders(accessToken)
    });

    if (!response.ok) {
      logger.error('Failed to download historical snapshot', {
        migrationId,
        timestamp,
        status: response.status,
        statusText: response.statusText
      });
      throw new Error(`Failed to download historical snapshot: ${response.statusText}`);
    }

    // Get the blob from the response
    const blob = await response.blob();

    // Extract filename from Content-Disposition header or use a default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `snapshot-${migrationId}-${timestamp}.zip`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch?.[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Create a temporary link element and trigger the download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    logger.info('Historical snapshot downloaded successfully', {
      migrationId,
      timestamp,
      filename
    });
  } catch (error) {
    logger.error('Error downloading historical snapshot', { migrationId, timestamp, error });
    throw error;
  }
}
